import { Link } from '@tanstack/react-router'
import { StatusPill } from '@/components/status-pill'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, formatMoney, formatPercent, parseUTCDate } from '@/lib/utils'
import { orderRecency } from '@/lib/customer-list'
import type { components } from '@/api/schema'

type Customer = components['schemas']['CustomerGroupListRead']
type LastOrder = components['schemas']['CustomerLastOrderRead']

function location(customer: Customer) {
  return [customer.city, customer.state].filter(Boolean).join(', ')
}

/** The company, unless it only repeats the customer's name. */
function distinctCompany(customer: Customer) {
  const company = customer.company_name?.trim()
  if (!company || company.toLowerCase() === customer.name.trim().toLowerCase()) return null
  return company
}

/** The project name, unless it only repeats the order ("Shopify Order #1364"). */
function distinctProjectName(order: LastOrder) {
  const name = order.project?.name
  if (!name) return null
  if (order.order_number && name.includes(order.order_number)) return null
  return name
}

function orderLabel(order: LastOrder) {
  return order.order_number ? `#${order.order_number}` : (order.name ?? 'Order')
}

function formatDate(value: string) {
  return parseUTCDate(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function countLabel(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function RecencyPill({ customer }: { customer: Customer }) {
  const recency = orderRecency(customer.last_order?.created_at)
  return <StatusPill tone={recency.tone}>{recency.label}</StatusPill>
}

function Profit({ customer }: { customer: Customer }) {
  const profit = Number(customer.profit)
  return (
    <p className={cn('text-[11px] text-muted-foreground', profit < 0 && 'text-red-700')}>
      {formatMoney(customer.profit)}
      {customer.margin != null && <> · {formatPercent(customer.margin)}</>}
    </p>
  )
}

function Counts({ customer }: { customer: Customer }) {
  return (
    <p className="text-[11px] text-muted-foreground">
      {countLabel(customer.order_count, 'order')} ·{' '}
      {countLabel(customer.project_count, 'project')}
    </p>
  )
}

function LastOrderCell({ order }: { order: LastOrder }) {
  const projectName = distinctProjectName(order)
  return (
    <div className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <Link
          to="/orders/$orderId"
          params={{ orderId: order.uuid }}
          className="font-medium text-primary hover:underline"
        >
          {orderLabel(order)}
        </Link>
        {order.project_status && (
          <StatusPill tone={order.project_status.tone}>{order.project_status.label}</StatusPill>
        )}
      </div>
      {projectName && order.project && (
        <p className="text-xs">
          <Link
            to="/projects/$projectId"
            params={{ projectId: order.project.uuid }}
            className="text-muted-foreground hover:text-primary hover:underline"
          >
            {projectName}
          </Link>
        </p>
      )}
      <p className="text-xs text-muted-foreground tabular-nums">
        {formatDate(order.created_at)} · {formatMoney(order.revenue)}
      </p>
    </div>
  )
}

export function CustomersTableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Loading customers">
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Last order</TableHead>
              <TableHead className="text-right">Sales</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rows }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <Skeleton className="h-3 w-40" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-end gap-1">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-2 lg:hidden">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-2 rounded-lg border bg-card p-3">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-5 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function CustomersTable({ items }: { items: Customer[] }) {
  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Last order</TableHead>
              <TableHead className="text-right">Sales</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((customer) => {
              const company = distinctCompany(customer)
              const place = location(customer)
              return (
                <TableRow key={customer.id} className="align-top">
                  <TableCell className="max-w-[300px] whitespace-normal">
                    <div className="space-y-0.5">
                      <Link
                        to="/customers/$groupId"
                        params={{ groupId: customer.uuid }}
                        className="font-medium text-primary hover:underline"
                      >
                        {customer.name}
                      </Link>
                      {company && <p className="text-xs">{company}</p>}
                      {place && <p className="text-xs text-muted-foreground">{place}</p>}
                      <div className="pt-0.5">
                        <RecencyPill customer={customer} />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[260px] whitespace-normal">
                    {customer.email || customer.phone ? (
                      <div className="space-y-0.5 text-xs">
                        {customer.email && (
                          <p className="break-all text-muted-foreground">{customer.email}</p>
                        )}
                        {customer.phone && (
                          <p className="text-muted-foreground tabular-nums">{customer.phone}</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[340px] whitespace-normal">
                    {customer.last_order ? (
                      <LastOrderCell order={customer.last_order} />
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <p className="text-sm font-semibold">{formatMoney(customer.revenue)}</p>
                    <Profit customer={customer} />
                    <Counts customer={customer} />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Mobile and tablet cards */}
      <div className="space-y-2 lg:hidden">
        {items.map((customer) => {
          const company = distinctCompany(customer)
          const place = location(customer)
          const order = customer.last_order
          return (
            <Link
              key={customer.id}
              to="/customers/$groupId"
              params={{ groupId: customer.uuid }}
              className="block rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="break-words font-medium text-primary">{customer.name}</p>
                  {company && <p className="truncate text-xs">{company}</p>}
                  {place && <p className="truncate text-xs text-muted-foreground">{place}</p>}
                </div>
                <div className="shrink-0 text-right tabular-nums">
                  <p className="text-sm font-semibold">{formatMoney(customer.revenue)}</p>
                  <Profit customer={customer} />
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <RecencyPill customer={customer} />
                {order && (
                  <>
                    <span className="text-xs font-medium">{orderLabel(order)}</span>
                    {order.project_status && (
                      <StatusPill tone={order.project_status.tone}>
                        {order.project_status.label}
                      </StatusPill>
                    )}
                    <span className="text-[11px] text-muted-foreground">
                      {formatDate(order.created_at)}
                    </span>
                  </>
                )}
              </div>

              <div className="mt-2 border-t pt-2">
                <Counts customer={customer} />
              </div>
            </Link>
          )
        })}
      </div>
    </>
  )
}
